import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { request, gql } from "graphql-request";
import { hyGraphToken, endpointURL } from "../ThemeModule";
import BlogSinglePageHeader from "./blog/BlogSinglePageHeader";

import { ParallaxBanner } from "react-scroll-parallax";
import Sidebar from "./blog/Sidebar";
import Seo from "../components/Seo";
import {
  SITE_URL,
  breadcrumbSchema,
  toDescription,
} from "../seo/siteMeta.mjs";

const BlogDetails = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      const endpoint = endpointURL;
      const query = gql`
        query GetPostBySlug($slug: String!) {
          post(where: { slug: $slug }) {
            title
            slug
            excerpt
            updatedAt
            content {
              html
            }
            author {
              title
              profileImage {
                url
              }
            }
            categories {
              title
            }
            publishedAt
            featureImage {
              url
            }
          }
        }
      `;

      const headers = {
        Authorization: `Bearer ${hyGraphToken}`,
      };

      try {
        const { post } = await request(endpoint, query, { slug }, headers);
        setPost(post);
      } catch (error) {
        console.error("Error fetching post:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  const articleSchema = post
    ? [
        {
          "@type": "BlogPosting",
          "@id": `${SITE_URL}/blog/${slug}#article`,
          headline: post.title.trim(),
          description: toDescription(post.excerpt),
          image: post.featureImage?.url ? [post.featureImage.url] : undefined,
          datePublished: post.publishedAt,
          dateModified: post.updatedAt || post.publishedAt,
          author: post.author?.title
            ? { "@type": "Person", name: post.author.title }
            : { "@id": `${SITE_URL}/#organization` },
          publisher: { "@id": `${SITE_URL}/#organization` },
          mainEntityOfPage: `${SITE_URL}/blog/${slug}`,
          articleSection: (post.categories || []).map((c) => c.title),
          isPartOf: { "@id": `${SITE_URL}/#website` },
        },
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: post.title.trim(), path: `/blog/${slug}` },
        ]),
      ]
    : [];

  return (
    <>
      {!loading && !post && (
        <Seo
          path={`/blog/${slug}`}
          title="Article Not Found"
          description="This article is no longer available."
          noindex
        />
      )}
      {post && (
        <Seo
          path={`/blog/${slug}`}
          title={post.title.trim()}
          description={toDescription(post.excerpt)}
          type="article"
          image={post.featureImage?.url}
          imageAlt={post.title.trim()}
          publishedTime={post.publishedAt}
          modifiedTime={post.updatedAt || post.publishedAt}
          author={post.author?.title}
          sections={(post.categories || []).map((c) => c.title)}
          schema={articleSchema}
        />
      )}

      <BlogSinglePageHeader
        post={post}
        title={post ? post.title : "Loading..."}
      />

      {loading ? (
        <div className="loader text-center">Loading...</div>
      ) : error ? (
        <div className="error-message text-red-500 text-center">
          <p>Failed to load post: {error}</p>
        </div>
      ) : (
        post && (
          <div className="post-details">
            <ParallaxBanner
              layers={[{ image: post.featureImage.url, speed: -15 }]}
              className="aspect-[3/1]"
            />
            <div className="max-w-container shape-1 shape-2 relative mx-auto px-4 pt-24 pb-20">
              <div className="flex-wrap flex justify-left  -mx-6">
                <div className="w-full md:w-8/12 px-6">
                  <div
                    className="post-content relative  "
                    dangerouslySetInnerHTML={{ __html: post.content.html }}
                  ></div>
                </div>
                <div className="w-full md:w-4/12 px-6 ">
                  <Sidebar />
                </div>
              </div>
            </div>
          </div>
        )
      )}
    </>
  );
};

export default BlogDetails;
